import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Box,
  TextField,
  IconButton,
  FormLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody
} from '@mui/material'
import { Button } from '@/@core/components/mui/Button'
import DialogCloseButton from '@/components/dialog/DialogCloseButton'
import { useTranslator } from '@/hooks/useTranslator'
import { FormProvider, useForm, Controller } from 'react-hook-form'
import { useRef, useState, useMemo, useEffect } from 'react'
import { useRouteQuery } from '@/hooks/useRouteQuery'
import { useNotification } from '@/hooks'
import { useParams } from 'react-router-dom'
import { Attendances, IHttpError, useUploadAttendanceReconcileByCompanyIdMutation } from '@/@data'
import { useQuery, useQueryClient } from '@tanstack/react-query'

export const UploadCompanyAttendanceCVS = ({
  open,
  onClose,
  onComplete
}: {
  open: boolean
  onClose?: () => void
  onComplete?: () => void
}) => {
  const { __ } = useTranslator()
  const { searchQuery } = useRouteQuery()
  const n = useNotification()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const { id } = useParams<{ id: string }>()
  const [checkUpload, setCheckUpload] = useState<boolean>(false)
  const [shouldPoll, setShouldPoll] = useState<boolean>(false)
  const [isError, setIsError] = useState<boolean>(false)
  const [isDownloading, setIsDownloading] = useState<boolean>(false)

  const methods = useForm({
    defaultValues: {
      month: '',
      file: null
    }
  })

  const handleChooseFile = () => {
    fileInputRef.current?.click()
  }

  const handleClearFile = () => {
    methods.setValue('file', null)

    if (fileInputRef.current) {
      fileInputRef.current.value = ''
    }
  }

  const { mutate: uploadAttendanceReconcile, isPending: isUploading } =
    useUploadAttendanceReconcileByCompanyIdMutation()

  const queryClient = useQueryClient()

  const onSubmit = async (data: any) => {
    const formData = new FormData()

    formData.append('month', searchQuery.get('month') || '')

    if (data.file && data.file[0]) {
      formData.append('file', data.file[0])
    }

    uploadAttendanceReconcile(
      {
        companyId: Number(searchQuery.get('companyId')) || Number(id),
        data: formData
      },
      {
        onSuccess: () => {
          setCheckUpload(true)
          setShouldPoll(true)
        },
        onError: (error: IHttpError) => {
          n.error(error.message)
        }
      }
    )
  }

  const params = useMemo(() => {
    return {
      companyId: Number(searchQuery.get('companyId')) || Number(id),
      month: searchQuery.get('month')
    }
  }, [id, searchQuery.get('month'), searchQuery.get('companyId')])

  const { data: checkUploadAttendanceReconcile } = useQuery({
    ...Attendances.checkUploadAttendanceReconcileByCompanyId(params),
    enabled: !!checkUpload,
    refetchInterval: shouldPoll ? 5000 : false
  })

  useEffect(() => {
    if (!checkUpload || !shouldPoll) return

    const response = checkUploadAttendanceReconcile as any
    const data = response?.data

    if (data?.status === 'completed') {
      setShouldPoll(false)
      setCheckUpload(false)
      onComplete?.()
    } else if (data?.status === 'failed') {
      setShouldPoll(false)
      setCheckUpload(false)
      setIsError(true)
    }
  }, [checkUploadAttendanceReconcile, checkUpload, shouldPoll, n, onClose])

  // Reset states dialog and clear stale cache for check query
  useEffect(() => {
    const checkQuery = Attendances.checkUploadAttendanceReconcileByCompanyId(params)

    if (!open) {
      setCheckUpload(false)
      setShouldPoll(false)
      setIsError(false)
      setIsDownloading(false)
      methods.reset()
      queryClient.removeQueries({ queryKey: checkQuery.queryKey })
    } else {
      setCheckUpload(false)
      setShouldPoll(false)
      setIsError(false)
      setIsDownloading(false)
      methods.reset()
      queryClient.removeQueries({ queryKey: checkQuery.queryKey })
    }
  }, [open])


  const handleDownloadReconcileTemplate = async () => {
    if (isDownloading) {
      return
    }

    // Validate params before making the request
    if (!params.companyId || !params.month) {
      n.error(__('Company ID or month is missing'))

      return
    }

    setIsDownloading(true)

    try {
      const response: any = await Attendances.exportAttendanceReconcileTemplate(params)

      // Handle different possible response structures
      // API returns IResponse<{ url: string }>, so structure is: { message, data: { url: string } }
      // Axios wraps it, so full path is: response.data.data.url
      const fileUrl = response.data?.data?.url || response.data?.url || response.url

      if (!fileUrl) {
        throw new Error('File URL not found in response')
      }

      const urlParts = fileUrl.split('/')
      const fileName = urlParts[urlParts.length - 1]
      const link = document.createElement('a')

      link.href = fileUrl
      link.setAttribute('download', fileName)
      link.setAttribute('target', '_blank')
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (error: any) {
      n.error(error?.message || __('Download failed'))
    } finally {
      setIsDownloading(false)
    }
  }

  return (
    <Dialog
      key={`dialog-${open}`}
      open={open}
      onClose={(_, reason) => {
        if (reason !== 'backdropClick') {
          onClose?.()
        }
      }}
      disableEscapeKeyDown
      maxWidth='sm'
      fullWidth
      sx={{
        '& .MuiDialog-paper': { overflow: 'visible', maxWidth: '400', borderRadius: '32px' }
      }}
    >
      <FormProvider {...methods}>
        <form onSubmit={methods.handleSubmit(onSubmit)}>
          <DialogTitle className='text-left text-[18px] font-medium'>
            {isError ? __('Invalid Template File') : __('Upload Company Attendance CVS')}
          </DialogTitle>

          <DialogContent>
            <DialogCloseButton onClick={onClose} disableRipple disabled={checkUpload}>
              <i className='tabler-x' />
            </DialogCloseButton>

            {!isError && (
              <Box className='space-y-4 mt-4'>
                <Box>
                  <Controller
                    name='file'
                    control={methods.control}
                    rules={{ required: __('File is required') }}
                    render={({ field, fieldState }: { field: any; fieldState: any }) => (
                      <Box className='mt-2'>
                        <FormLabel sx={{ '& .MuiFormLabel-asterisk': { color: 'red' } }}>{__('File')}</FormLabel>
                        <Box className='flex gap-2'>
                          <TextField
                            fullWidth
                            value={field.value && field.value.length > 0 ? field.value[0].name : ''}
                            placeholder={__('Choose file')}
                            variant='outlined'
                            size='small'
                            slotProps={{
                              input: {
                                readOnly: true,
                                style: { borderRadius: '20px' },
                                endAdornment: (
                                  <>
                                    {field.value && field.value.length > 0 && (
                                      <IconButton size='small' onClick={handleClearFile} aria-label='clear file'>
                                        <i className='tabler-circle-x text-[16px]' />
                                      </IconButton>
                                    )}
                                    <IconButton size='small' onClick={handleChooseFile} aria-label='upload file'>
                                      <i className='tabler-upload text-[16px]' />
                                    </IconButton>
                                  </>
                                )
                              }
                            }}
                            error={!!fieldState.error}
                            helperText={fieldState.error?.message}
                            disabled
                          />
                          <Button
                            variant='tonal'
                            onClick={handleChooseFile}
                            className='min-w-[90px] rounded-[20px]'
                            size='small'
                            disabled={checkUpload}
                          >
                            <span className='text-[14px] font-medium'>{__('Choose')}</span>
                          </Button>
                        </Box>
                        <input
                          ref={fileInputRef}
                          type='file'
                          accept='.xlsx,.xls'
                          style={{ display: 'none' }}
                          onChange={e => {
                            const files = e.target.files

                            field.onChange(files)
                          }}
                        />
                      </Box>
                    )}
                  />
                </Box>
              </Box>
            )}
            {isError && (
              <Box className='mt-4 rounded-[20px] border border-gray-200 overflow-hidden'>
                <Box sx={{ maxHeight: 350, overflowY: 'auto' }}>
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell className='text-[13px] font-medium' sx={{ width: '50px' }}>
                          #
                        </TableCell>
                        <TableCell className='text-[13px] font-medium' sx={{ width: '80px' }}>
                          {__('ROW')}
                        </TableCell>
                        <TableCell className='text-[13px] font-medium'>{__('INVALID REASON')}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {checkUploadAttendanceReconcile?.data?.data?.errors?.map((error: any, index: number) => (
                        <TableRow key={index}>
                          <TableCell className='text-[13px] font-medium' sx={{ width: '50px' }}>
                            {index + 1}
                          </TableCell>
                          <TableCell className='text-[13px] font-medium' sx={{ width: '80px' }}>
                            {error.row || '-'}
                          </TableCell>
                          <TableCell className='text-[13px] font-medium'>{error.error || '-'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Box>
              </Box>
            )}
          </DialogContent>
          <DialogActions className='flex justify-end'>
            {checkUpload && (
              <Button variant='contained' type='button' size='small' className='bg-[#97939e] shadow-none' disabled>
                <div className='flex items-center gap-2'>
                  <i className='tabler-loader animate-spin text-[16px]' />
                  <span className='text-[15px] font-medium'>{__('Waiting Upload File')}</span>
                </div>
              </Button>
            )}
            {!isError && (
              <Button
                variant='contained'
                className='text-[#fff] text-[15px] font-medium leading-[22px] bg-[#97939E] shadow-none rounded-full'
                startIcon={isDownloading ? <i className='tabler-loader animate-spin text-[16px]' /> : <i className='tabler-download fa-download'></i>}
                onClick={handleDownloadReconcileTemplate}
                disabled={checkUpload || isDownloading}
              >
                <span className='text-[15px] font-medium'>
                  {__('Download Template')}
                </span>
              </Button>
            )}
            {!checkUpload && !isError && (
              <Button variant='contained' color='primary' type='submit' className='w-[90px]' disabled={isUploading}>
                <span className='text-[15px] font-medium'>
                  {isUploading ? <i className='tabler-loader animate-spin text-[16px]' /> : __('Upload')}
                </span>
              </Button>
            )}
            {isError && (
              <Button
                variant='contained'
                color='primary'
                type='submit'
                onClick={() => {
                  navigator.clipboard.writeText(
                    checkUploadAttendanceReconcile?.data?.data?.errors
                      ?.map((error: any) => `Row ${error?.row}: ${error?.error}`)
                      .join('\n')
                  )
                  n.success(__('Copied to clipboard'))
                }}
              >
                <span className='text-[15px] font-medium'>{__('Copy All Errors ')}</span>
              </Button>
            )}
            <Button
              onClick={onClose}
              variant='outlined'
              type='button'
              size='small'
              className='w-[90px]'
              disabled={checkUpload}
            >
              <span className='text-[15px] font-medium'>{__('Cancel')}</span>
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  )
}
